import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  getDashboard,
  getEnrollments,
  getWorkflowActions,
  getMemberData,
  isAuthenticated,
  logoutMember,
  getMemberToken,
  type MemberDashboard,
  type WorkflowAction,
} from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useMemberTimezone } from '@/hooks/useMemberTimezone';
import { formatDateInTimezone } from '@/utils/dateTime';
import { browserTimezone } from '@/lib/memberPreferences';
import { PageContainer, PageState, PortalHero } from '@/components/weavein';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  FileText,
  AlertCircle,
  CheckCircle,
  ClipboardList,
  Bell,
  Gift,
  Star,
  Trophy,
  DollarSign,
  Target,
  TrendingUp,
  Award,
  Zap,
  CreditCard,
  Building2,
  Sparkles,
  ArrowRight,
  Compass,
  Bot,
  ChevronRight,
  Shield,
  Users,
  ListChecks,
  Workflow,
  Circle,
  CircleDot,
  HeartPulse,
  Calendar,
  ListTodo,
  Clock,
  Flag,
  CalendarClock,
} from 'lucide-react';

// One step within a workflow instance's history. The backend now populates this
// from the workflow template, so `name`, `step_type` and a normalized `status`
// ('completed' | 'in_progress' | 'upcoming') are present per step.
interface WorkflowStepEntry {
  step: number;
  status?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  name?: string | null;
  step_type?: string | null;
  result?: string | null;
}

// A member's workflow instance, as served bare by
// GET /api/member-portal/workflow-steps. Drives the workflow detail modal.
interface WorkflowInstance {
  id: number;
  enrollment_id?: number | null;
  status?: string | null;
  current_step?: number | null;
  current_step_name?: string | null;
  total_steps?: number | null;
  program_name?: string | null;
  workflow_name?: string | null;
  created_at?: string | null;
  steps: WorkflowStepEntry[];
}

interface IncentiveSummary {
  actions: {
    total: number;
    completed: number;
    total_points: number;
    potential_earnings: number;
  };
  earnings: {
    total_earnings: number;
    total_paid: number;
    pending_amount: number;
    total_points_earned: number;
  };
  recent_achievements: Array<{
    type: string;
    name: string;
    level: string | null;
    points: number;
    earned_date: string;
  }>;
}

interface MemberCard {
  id: number;
  card_number_last4: string;
  card_status: string;
  remaining_balance: number;
  spending_limit_amount: number;
  current_period_spending: number;
  expiration_month: string;
  expiration_year: string;
}

interface EmployeeData {
  id: number;
  employee_number: string;
  first_name: string;
  last_name: string;
  employer_id: number;
  employer_name?: string;
  job_title?: string;
  department?: string;
}

interface BenefitProgram {
  id: number;
  program_code: string;
  program_name: string;
  program_type: string;
  program_category: string;
  status: string;
  enrollment_status?: string;
}

interface Recommendation {
  id: number;
  program_name: string;
  program_category?: string;
  score: number;
  reasons: string[];
  status: string;
}

// Shape rendered by the "My Programs" card — a defensive subset of the backend
// EnrollmentDetail model (id, program_name, status, enrollment_date, …).
interface MyProgram {
  id: number;
  program_name?: string;
  program_description?: string | null;
  status?: string;
  enrollment_date?: string;
  completion_date?: string | null;
  progress_percentage?: number | null;
  notes?: string | null;
  // The owning organization for this enrollment. Enrollments now span ALL of the
  // member's orgs, so each row is labelled with its org.
  organization_name?: string | null;
}

// A member's care case episode, as served bare by
// GET /api/member-portal/case-episodes. Read-only; may be empty.
interface CaseEpisode {
  id: number;
  case_number?: string | null;
  episode_number?: number | string | null;
  status?: string | null;
  is_current?: boolean;
  opened_date?: string | null;
  closed_date?: string | null;
  service_period_start?: string | null;
  service_period_end?: string | null;
  program_name?: string | null;
  organization_name?: string | null;
  care_manager_name?: string | null;
}

// A member-facing task, as served bare by GET /api/member-portal/tasks. Powers
// the "To-do / Schedule" section, the task detail modal, and the per-case
// schedule of tasks. Read-only; may be empty.
interface MemberTask {
  id: number;
  title?: string | null;
  description?: string | null;
  task_type?: string | null;
  status?: string | null;
  priority?: string | null;
  due_at?: string | null;
  progress_percentage?: number | null;
  completed_at?: string | null;
  program_id?: number | null;
  program_name?: string | null;
  case_episode_id?: number | null;
  workflow_instance_id?: number | null;
  organization_name?: string | null;
  created_at?: string | null;
}

// Employer-benefits shapes — served for the employee benefits portal and
// callable with the member token. All money fields may arrive as strings/Decimal.
interface BenefitsAccumulator {
  deductible_met?: number | string | null;
  deductible_total?: number | string | null;
  oop_met?: number | string | null;
  oop_max?: number | string | null;
  plan_type?: string | null;
}

interface BenefitAccount {
  account_type: string; // 'HSA' | 'FSA' | 'HRA' | 'DCFSA'
  balance?: number | string | null;
  available_balance?: number | string | null;
  ytd_contributions?: number | string | null;
  employer_contributions?: number | string | null;
  annual_limit?: number | string | null;
  use_by_date?: string | null;
  account_number_last4?: string | null;
}

interface RecentClaim {
  id: number | string;
  claim_number?: string;
  service_date?: string | null;
  provider_name?: string | null;
  service_type?: string | null;
  patient_responsibility?: number | string | null;
  status?: string | null;
  eob_available?: boolean;
  eob_url?: string | null;
}

interface PlanCoverage {
  plan_type?: string | null;
  plan_name?: string | null;
  carrier?: string | null;
  coverage_tier?: string | null;
  status?: string | null;
  effective_date?: string | null;
  copay_pcp?: number | string | null;
  copay_specialist?: number | string | null;
  deductible_individual?: number | string | null;
  deductible_family?: number | string | null;
  oop_max_individual?: number | string | null;
  oop_max_family?: number | string | null;
}

// Coerce a possibly-string/Decimal/null value to a number (NaN-safe).
const toNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Currency formatter for benefit amounts (USD, 2 decimals).
const fmtCurrency = (v: unknown): string =>
  toNum(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Percentage of `met` against `total`, clamped 0–100. Returns 0 when total <= 0.
const pctOf = (met: unknown, total: unknown): number => {
  const t = toNum(total);
  if (t <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((toNum(met) / t) * 100)));
};

// Short human date (e.g. "Nov 15, 2025") for a date-ish string, rendered in the
// member's timezone; '' when unparseable.
const fmtShortDate = (v?: string | null, timezone?: string): string => {
  if (!v) return '';
  if (Number.isNaN(new Date(v).getTime())) return '';
  return formatDateInTimezone(v, timezone || browserTimezone(), 'MMM d, yyyy');
};

// A task counts as "done" (drops to the bottom of the schedule, renders muted)
// when its status is any terminal value. Everything else is an open to-do.
const isTaskDone = (status?: string | null): boolean =>
  ['completed', 'complete', 'done', 'closed', 'cancelled', 'canceled'].includes(
    String(status || '').toLowerCase()
  );

// Schedule order: open items first, then by soonest due date (undated last),
// with completed items sinking to the bottom. Non-mutating.
const sortTasksBySchedule = (list: MemberTask[]): MemberTask[] =>
  list.slice().sort((a, b) => {
    const ad = isTaskDone(a.status);
    const bd = isTaskDone(b.status);
    if (ad !== bd) return ad ? 1 : -1;
    const at = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
    const bt = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
    return at - bt;
  });

// Loose program-name match (case/space-insensitive) used to associate a
// workflow instance or task with a program/case that only carries a name.
const sameProgram = (a?: string | null, b?: string | null): boolean => {
  const na = String(a || '').trim().toLowerCase();
  const nb = String(b || '').trim().toLowerCase();
  return !!na && na === nb;
};

// Priority → badge classes (v2 tokens, dark-aware). Unknown priorities fall
// back to a neutral outline.
const priorityBadgeClass = (priority?: string | null): string => {
  switch (String(priority || '').toLowerCase()) {
    case 'urgent':
    case 'high':
      return 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700';
    case 'medium':
    case 'normal':
      return 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700';
    case 'low':
      return 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600';
    default:
      return 'bg-paper-2 text-ink-500 border-v2border';
  }
};

// Ordered checklist for a workflow instance's steps — completed ✓ / current • /
// upcoming ○, with step_type and any completed/started date. Reused by both the
// workflow detail modal and the per-case "Schedule of tasks". Renders nothing
// when the instance has no populated steps.
function WorkflowStepsList({
  instance,
  timezone,
}: {
  instance: WorkflowInstance;
  timezone?: string;
}) {
  const { t } = useAppTranslation();
  if (!instance.steps || instance.steps.length === 0) return null;
  return (
    <div className="space-y-1 max-h-72 overflow-y-auto">
      {instance.steps
        .slice()
        .sort((a, b) => (a.step ?? 0) - (b.step ?? 0))
        .map((s) => {
          const statusStr = String(s.status || '').toLowerCase();
          const isDone =
            !!s.completed_at ||
            ['completed', 'complete', 'done', 'passed'].includes(statusStr) ||
            (instance.current_step != null && s.step < instance.current_step);
          const isCurrent =
            !isDone &&
            (statusStr === 'in_progress' ||
              statusStr === 'in progress' ||
              statusStr === 'active' ||
              (instance.current_step != null && s.step === instance.current_step));
          const when = s.completed_at || s.started_at;
          return (
            <div
              key={s.step}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                isCurrent
                  ? 'border-teal-300 dark:border-teal-700 bg-teal-50/60 dark:bg-teal-900/20'
                  : 'border-v2border bg-paper/50'
              }`}
            >
              <div className="mt-0.5 flex-shrink-0">
                {isDone ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : isCurrent ? (
                  <CircleDot className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                ) : (
                  <Circle className="w-4 h-4 text-ink-300 dark:text-ink-500" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">
                  {s.step}. {s.name || `${t('dashboard.step', 'Step')} ${s.step}`}
                  {isCurrent && (
                    <span className="ml-2 text-xs font-normal text-teal-600 dark:text-teal-400">
                      ({t('dashboard.current', 'current')})
                    </span>
                  )}
                </p>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-ink-500 capitalize truncate">
                    {[
                      s.step_type ? String(s.step_type).replace(/_/g, ' ') : '',
                      s.status ? String(s.status).replace(/_/g, ' ') : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {fmtShortDate(when, timezone) && (
                    <span className="text-xs text-ink-500 flex-shrink-0">
                      {fmtShortDate(when, timezone)}
                    </span>
                  )}
                </div>
                {s.result && (
                  <p className="text-xs text-ink-500 mt-0.5 truncate">{s.result}</p>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}

export default function MemberDashboardPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const timezone = useMemberTimezone();
  const [dashboard, setDashboard] = useState<MemberDashboard | null>(null);
  const [incentives, setIncentives] = useState<IncentiveSummary | null>(null);
  const [card, setCard] = useState<MemberCard | null>(null);
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [benefitPrograms, setBenefitPrograms] = useState<BenefitProgram[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  // Employer-benefits usage summary (OOP / HSA / FSA / EOB) + plan coverages —
  // all real, best-effort. Missing data renders an empty state (never fake numbers).
  const [accumulator, setAccumulator] = useState<BenefitsAccumulator | null>(null);
  const [hsaAccount, setHsaAccount] = useState<BenefitAccount | null>(null);
  const [fsaAccount, setFsaAccount] = useState<BenefitAccount | null>(null);
  const [recentClaims, setRecentClaims] = useState<RecentClaim[]>([]);
  const [planCoverages, setPlanCoverages] = useState<PlanCoverage[]>([]);
  // My Programs (real enrollments) + Action needed (real workflow actions) —
  // both mirror the staff web app and load independently of the main dashboard.
  const [enrollments, setEnrollments] = useState<MyProgram[]>([]);
  // Care case episode(s) — read-only care-management context (bare array).
  const [caseEpisodes, setCaseEpisodes] = useState<CaseEpisode[]>([]);
  const [workflowActions, setWorkflowActions] = useState<WorkflowAction[]>([]);
  // Full workflow-instance progress (steps + current position) for the detail
  // modal, keyed by instance id. Loaded alongside the action list.
  const [workflowInstances, setWorkflowInstances] = useState<WorkflowInstance[]>([]);
  // The task/workflow action whose detail modal is currently open (null = closed).
  const [detailAction, setDetailAction] = useState<WorkflowAction | null>(null);
  // Member tasks (bare array) — the dated to-do schedule + per-case schedule.
  const [tasks, setTasks] = useState<MemberTask[]>([]);
  // The program / case / task whose detail modal is currently open (null = closed).
  const [detailProgram, setDetailProgram] = useState<MyProgram | null>(null);
  const [detailCase, setDetailCase] = useState<CaseEpisode | null>(null);
  const [detailTask, setDetailTask] = useState<MemberTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Per-member feature availability (union across the member's active orgs) —
  // drives which dashboard tiles show. null = not loaded yet → show everything
  // (avoids a flash of hidden tiles); a feature only hides when explicitly false.
  const [features, setFeatures] = useState<Record<string, boolean> | null>(null);

  const member = getMemberData();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getMemberToken();
        if (!token) return;
        const res = await fetch(getWeaveinApiUrl('/api/member-portal/features'), {
          headers: {
            Authorization: `Bearer ${token}`,
            // Scope features to the active org so the switcher changes the view.
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setFeatures(data.features || null);
        }
      } catch {
        /* fail open — leave features null so all tiles remain visible */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load My Programs + Action-needed items. Each is best-effort so a failure in
  // one never blocks the dashboard; both route through memberAuth (token +
  // active-org X-CBO-ID header).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getMemberToken()) return;
      try {
        const data = await getEnrollments();
        if (!cancelled) setEnrollments(Array.isArray(data) ? (data as MyProgram[]) : []);
      } catch {
        /* leave empty — card shows its empty state */
      }
      try {
        const wf = await getWorkflowActions();
        if (!cancelled) setWorkflowActions(wf?.actions || []);
      } catch {
        /* leave empty — card hides when there are no actions */
      }
      // Workflow-instance progress (steps + current position) for the detail
      // modal. Member auth + active-org X-CBO-ID, inline (bare array response).
      try {
        const token = getMemberToken();
        if (token) {
          const res = await fetch(getWeaveinApiUrl('/api/member-portal/workflow-steps'), {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            },
          });
          if (res.ok && !cancelled) {
            const data = await res.json();
            setWorkflowInstances(Array.isArray(data) ? (data as WorkflowInstance[]) : []);
          }
        }
      } catch {
        /* leave empty — the modal falls back to the action's summary */
      }
      // Care case episode(s). Member auth + active-org X-CBO-ID, inline (bare
      // array response). Best-effort — the card hides when the array is empty.
      try {
        const token = getMemberToken();
        if (token) {
          const res = await fetch(getWeaveinApiUrl('/api/member-portal/case-episodes'), {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            },
          });
          if (res.ok && !cancelled) {
            const data = await res.json();
            setCaseEpisodes(Array.isArray(data) ? (data as CaseEpisode[]) : []);
          }
        }
      } catch {
        /* leave empty — the Care case card hides when there are no episodes */
      }
      // Member tasks — the dated to-do schedule and per-case task list. Member
      // auth + active-org X-CBO-ID, inline (bare array response). Best-effort.
      try {
        const token = getMemberToken();
        if (token) {
          const res = await fetch(getWeaveinApiUrl('/api/member-portal/tasks'), {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            },
          });
          if (res.ok && !cancelled) {
            const data = await res.json();
            setTasks(Array.isArray(data) ? (data as MemberTask[]) : []);
          }
        }
      } catch {
        /* leave empty — the To-do section shows its empty state */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Route a workflow action by its `target` hint.
  const handleWorkflowAction = (action: WorkflowAction) => {
    const target = action.target || '';
    if (target.startsWith('task:') || target.startsWith('workflow:')) {
      // Defensive no-op: this handler only drives the "Action needed" nudges,
      // which never include task/workflow items. Tasks live in "To-do /
      // Schedule" and workflows in "Workflow steps" (each with its own modal).
      return;
    }
    if (target.startsWith('comm:') || target.startsWith('channel:')) {
      navigate(`/member/inbox?thread=${encodeURIComponent(target)}`);
    } else if (target === 'training') {
      navigate('/member/training');
    } else if (target === 'programs') {
      navigate('/member/enrollments');
    } else {
      navigate('/member/inbox');
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Check authentication on mount
    const checkAuthAndLoadData = async () => {
      try {
        // Check if user is logged in as organization/staff instead of member
        const orgToken = localStorage.getItem('auth_token');
        if (orgToken && !member) {
          console.log('Organization user attempting to access member dashboard');
          alert(t('dashboard.membersOnlyRedirect', 'This page is for members only. Redirecting to organization dashboard.'));
          navigate('/organizations', { replace: true });
          return;
        }

        if (!isAuthenticated()) {
          console.log('Not authenticated, redirecting to login');
          navigate('/member/login', { replace: true });
          return;
        }

        // Verify member data exists
        if (!member) {
          console.log('No member data found, logging out');
          logoutMember();
          navigate('/member/login', { replace: true });
          return;
        }

        // Load dashboard data
        console.log('Loading dashboard data for member:', member.email);
        const data = await getDashboard();
        setDashboard(data);

        // Load incentive summary data and card data with timeout protection
        if (member.id) {
          const token = getMemberToken();
          if (token) {
            // Helper function to fetch with timeout
            const fetchWithTimeout = async (url: string, options: RequestInit, timeoutMs = 5000) => {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
              try {
                const response = await fetch(url, { ...options, signal: controller.signal });
                clearTimeout(timeoutId);
                return response;
              } catch (err) {
                clearTimeout(timeoutId);
                throw err;
              }
            };

            // Fetch incentives
            try {
              const incentivesUrl = getWeaveinApiUrl(`/api/incentives/members/${member.id}/summary`);
              const incentivesResponse = await fetchWithTimeout(incentivesUrl, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                  'Content-Type': 'application/json'
                }
              }, 5000);

              if (incentivesResponse.ok) {
                const incentivesData = await incentivesResponse.json();
                setIncentives(incentivesData);
              } else {
                console.warn('Failed to load incentive summary:', incentivesResponse.status);
              }
            } catch (incentiveErr) {
              console.warn('Could not load incentives:', incentiveErr);
              // Don't fail the whole dashboard if incentives fail
            }

            // Fetch card data
            try {
              const cardUrl = getWeaveinApiUrl(`/api/cards/member/${member.id}`);
              const cardResponse = await fetchWithTimeout(cardUrl, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                  'Content-Type': 'application/json'
                }
              }, 5000);

              if (cardResponse.ok) {
                const cardData = await cardResponse.json();
                // API returns an array of cards, get the first active one
                const activeCard = Array.isArray(cardData)
                  ? cardData.find(c => c.card_status?.toLowerCase() === 'active') || cardData[0]
                  : cardData;

                // Ensure numeric fields are numbers (backend may return strings)
                if (activeCard) {
                  activeCard.remaining_balance = Number(activeCard.remaining_balance) || 0;
                  activeCard.spending_limit_amount = Number(activeCard.spending_limit_amount) || 0;
                  activeCard.current_period_spending = Number(activeCard.current_period_spending) || 0;
                }

                setCard(activeCard || null);
              } else if (cardResponse.status !== 404) {
                // 404 means no card issued yet, which is fine
                console.warn('Failed to load card data:', cardResponse.status);
              }
            } catch (cardErr) {
              console.warn('Could not load card data:', cardErr);
              // Don't fail the whole dashboard if card fetch fails
            }

            // Fetch employee data (check if member is linked to an employer)
            // If member is an employee, redirect to the unified employee portal
            try {
              const employeeUrl = getWeaveinApiUrl(`/api/employees/by-member/${member.id}`);
              const employeeResponse = await fetchWithTimeout(employeeUrl, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                  'Content-Type': 'application/json'
                }
              }, 3000); // Short 3s timeout for optional data

              if (employeeResponse.ok) {
                const empData = await employeeResponse.json();

                // Only redirect EMPLOYER-ONLY members to the dedicated employee
                // portal. A member who also has a CBO relationship (dual/multi-org,
                // e.g. Trust Care + an employer) stays on the member dashboard and
                // sees employer benefits as a section below — otherwise their CBO
                // context (training, services, etc.) would be hidden entirely.
                let memberOrgs: unknown[] = [];
                try {
                  memberOrgs = JSON.parse(localStorage.getItem('member_organizations') || '[]');
                } catch {
                  memberOrgs = [];
                }
                const hasCboRelationship = Array.isArray(memberOrgs) && memberOrgs.length > 1;
                if (empData.employer_id && !hasCboRelationship) {
                  console.log('Employer-only member, redirecting to employee portal');
                  navigate('/employee-portal/dashboard', { replace: true });
                  return;
                }

                setEmployeeData(empData);

                // Benefits Usage Summary (OOP / HSA / FSA / EOB) — real data for the
                // employee benefits portal. Best-effort & parallel; each failure just
                // leaves its card empty (never falls back to fake numbers).
                if (empData.id) {
                  const benefitsHeaders = {
                    'Authorization': `Bearer ${token}`,
                    'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                    'Content-Type': 'application/json',
                  };
                  const fetchJson = async (path: string) => {
                    const resp = await fetchWithTimeout(getWeaveinApiUrl(path), { headers: benefitsHeaders }, 3000);
                    if (!resp.ok) return null;
                    return resp.json();
                  };

                  await Promise.allSettled([
                    (async () => {
                      try {
                        const acc = await fetchJson(`/api/employees/${empData.id}/benefits-accumulator`);
                        if (acc && !cancelled) setAccumulator(acc);
                      } catch (e) {
                        console.warn('Could not load benefits accumulator:', e);
                      }
                    })(),
                    (async () => {
                      try {
                        const accounts = await fetchJson(`/api/employees/${empData.id}/benefit-accounts`);
                        if (Array.isArray(accounts) && !cancelled) {
                          const norm = (t2: string) => (t2 || '').toUpperCase();
                          setHsaAccount(accounts.find((a: BenefitAccount) => norm(a.account_type) === 'HSA') || null);
                          setFsaAccount(accounts.find((a: BenefitAccount) => norm(a.account_type) === 'FSA') || null);
                        }
                      } catch (e) {
                        console.warn('Could not load benefit accounts:', e);
                      }
                    })(),
                    (async () => {
                      try {
                        const claims = await fetchJson(`/api/employees/${empData.id}/recent-claims`);
                        if (Array.isArray(claims) && !cancelled) setRecentClaims(claims);
                      } catch (e) {
                        console.warn('Could not load recent claims:', e);
                      }
                    })(),
                    (async () => {
                      try {
                        const coverages = await fetchJson(`/api/employees/${empData.id}/plan-coverages`);
                        if (Array.isArray(coverages) && !cancelled) setPlanCoverages(coverages);
                      } catch (e) {
                        console.warn('Could not load plan coverages:', e);
                      }
                    })(),
                  ]);
                }

                // If employee has an employer, fetch benefit programs and recommendations
                // Note: This code path is only reached if employer_id check above fails
                if (empData.employer_id) {
                  // Fetch benefit programs with enrollment status
                  try {
                    const benefitsUrl = getWeaveinApiUrl('/api/member-portal/benefits');
                    const benefitsResponse = await fetchWithTimeout(benefitsUrl, {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                        'Content-Type': 'application/json'
                      }
                    }, 3000);
                    if (benefitsResponse.ok) {
                      const programsData = await benefitsResponse.json();
                      setBenefitPrograms(programsData.slice(0, 4)); // Show top 4
                    }
                  } catch (progErr) {
                    console.warn('Could not load benefit programs:', progErr);
                  }

                  // Fetch recommendations
                  try {
                    const recsUrl = getWeaveinApiUrl(`/api/employees/${empData.id}/recommendations`);
                    const recsResponse = await fetchWithTimeout(recsUrl, {
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
                        'Content-Type': 'application/json'
                      }
                    }, 3000);
                    if (recsResponse.ok) {
                      const recsData = await recsResponse.json();
                      setRecommendations(recsData.slice(0, 3)); // Show top 3
                    }
                  } catch (recErr) {
                    console.warn('Could not load recommendations:', recErr);
                  }
                }
              }
              // 404 means member is not linked to any employer, which is fine
            } catch (empErr) {
              console.warn('Could not load employee data:', empErr);
              // Don't fail the whole dashboard if employee fetch fails
            }
          }
        }

        setError('');
      } catch (err: any) {
        console.error('Dashboard load error:', err);

        // If unauthorized, redirect to login
        if (err.message?.includes('Not authenticated') || err.message?.includes('Unauthorized')) {
          logoutMember();
          navigate('/member/login', { replace: true });
          return;
        }

        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndLoadData();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Split workflow actions across three distinct surfaces so no item is ever
  // duplicated:
  //   • workflow steps (the active workflow's current step, e.g. a Medicaid
  //     re-determination consent flow) → the "Workflow steps" section;
  //   • lighter nudges (replies, training invites, enrollment prompts) → the
  //     "Action needed" card;
  //   • task:<id> actions are intentionally dropped here — tasks live solely in
  //     the "To-do / Schedule" section, fed by /api/member-portal/tasks, so a
  //     task never appears in two places.
  const workflowStepActions = workflowActions.filter((a) => a.type === 'workflow');
  const nudgeActions = workflowActions.filter(
    (a) => a.type !== 'task' && a.type !== 'workflow'
  );

  // For an open workflow:<id> action, resolve the full instance (steps +
  // current position). task:<id> actions have no detail endpoint, so the modal
  // just shows what the action itself carries (title + subtitle).
  const detailWorkflowId =
    detailAction?.target?.startsWith('workflow:')
      ? Number(detailAction.target.slice('workflow:'.length))
      : null;
  const detailInstance =
    detailWorkflowId != null
      ? workflowInstances.find((w) => w.id === detailWorkflowId) || null
      : null;

  // The tasks scheduled against the open case episode: those explicitly linked
  // by case_episode_id, else falling back to a program-name match. Ordered as a
  // dated schedule (open first, soonest due first).
  const caseTasks = detailCase
    ? sortTasksBySchedule(
        tasks.filter((tk) =>
          tk.case_episode_id != null
            ? tk.case_episode_id === detailCase.id
            : sameProgram(tk.program_name, detailCase.program_name)
        )
      )
    : [];
  // Workflow instances belonging to the open case's program — rendered as the
  // ordered step checklist inside the case's "Schedule of tasks".
  const caseWorkflows = detailCase
    ? workflowInstances.filter((w) => sameProgram(w.program_name, detailCase.program_name))
    : [];
  // Workflow instances belonging to the open program (step summary in its modal).
  const programWorkflows = detailProgram
    ? workflowInstances.filter((w) => sameProgram(w.program_name, detailProgram.program_name))
    : [];
  // The whole to-do schedule (all tasks, dated order).
  const scheduledTasks = sortTasksBySchedule(tasks);

  if (loading) {
    return (
      <PageContainer size="wide">
        <PageState variant="loading" />
      </PageContainer>
    );
  }

  return (
    <PageContainer size="wide">
        {/* Welcome hero */}
        <PortalHero
          variant="calm"
          title={t('dashboard.welcome', 'Welcome, {{firstName}} {{lastName}}', { firstName: member?.first_name, lastName: member?.last_name })}
          subtitle={t('dashboard.memberPortal', 'Member Portal')}
        />

        {/* Prepaid Card Display - Top Priority. Gated on the ACTIVE org's
            `cards` design feature: an org without prepaid cards (e.g. LCC) never
            shows this panel even if a stale card record exists. Shown while
            features load (null) to avoid a flash; hidden only when explicitly
            false. */}
        {card && card.card_status?.toLowerCase() === 'active' && features?.cards !== false && (
          <PortalHero
            variant="vibrant"
            icon={<CreditCard className="h-6 w-6 text-white" />}
            subtitle={t('dashboard.prepaidCard', 'Prepaid Card')}
            title={<span className="tracking-wider">•••• {card.card_number_last4}</span>}
            actions={
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {card.card_status}
              </Badge>
            }
          >
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-white/80 text-xs mb-1">{t('dashboard.balance', 'Balance')}</p>
                  <p className="text-xl font-bold">${card.remaining_balance.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-white/80 text-xs mb-1">{t('dashboard.spentThisPeriod', 'Spent This Period')}</p>
                  <p className="text-xl font-bold">${card.current_period_spending.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-white/80 text-xs mb-1">{t('dashboard.expires', 'Expires')}</p>
                  <p className="text-xl font-bold">{card.expiration_month}/{card.expiration_year}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/20 flex justify-between items-center">
                <p className="text-sm text-white/80">
                  {t('dashboard.spendingLimit', 'Spending Limit')}: ${card.spending_limit_amount.toFixed(2)}
                </p>
                <Link to="/member/incentives?tab=card">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                    {t('dashboard.viewDetails', 'View Details')} →
                  </Button>
                </Link>
              </div>
          </PortalHero>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Notifications */}
        {dashboard && dashboard.unread_communications > 0 && (
          <Alert className="mb-6 border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/30">
            <Bell className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            <AlertDescription className="dark:text-gray-200">
              {t('dashboard.unreadMessages', { count: dashboard.unread_communications })}{' '}
              <Link to="/member/inbox" className="font-medium text-teal-600 dark:text-teal-400 hover:underline">
                {t('dashboard.viewMessages', 'View messages')} →
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {dashboard && dashboard.pending_responses > 0 && (
          <Alert className="mb-6 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="dark:text-gray-200">
              {t('dashboard.pendingResponses', { count: dashboard.pending_responses })}{' '}
              <Link to="/member/inbox" className="font-medium text-amber-600 dark:text-amber-400 hover:underline">
                {t('dashboard.respondNow', 'Respond now')} →
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Workflow steps — the current step of any active workflow (e.g. a
            Medicaid re-determination consent flow). Scoped to the ACTIVE org by
            the backend. Task items are deliberately excluded here (they live in
            "To-do / Schedule"); this section holds workflows only, and is hidden
            entirely when there are none so it never shows an empty box. */}
        {workflowStepActions.length > 0 && (
          <div className="mb-8">
            <Card className="bg-surface border border-v2border">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-paper-2 rounded-lg">
                    <ListChecks className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-ink">
                      {t('dashboard.workflowStepsSection', 'Workflow steps')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('dashboard.workflowStepsSectionDesc', 'Your current workflow steps')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workflowStepActions.map((action, idx) => (
                    <button
                      key={`${action.target}-${idx}`}
                      type="button"
                      onClick={() => setDetailAction(action)}
                      className="w-full text-left flex items-start justify-between gap-3 p-4 bg-paper/50 rounded-lg border border-v2border hover:bg-paper-2 transition-colors"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="p-2 bg-paper-2 rounded-lg mt-0.5 flex-shrink-0">
                          <Workflow className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink">{action.title}</p>
                          {action.subtitle && (
                            <p className="text-xs text-ink-500 mt-0.5">{action.subtitle}</p>
                          )}
                        </div>
                      </div>
                      <span className="ml-2 flex flex-shrink-0 items-center text-sm font-medium text-teal-600 dark:text-teal-400">
                        {action.action || t('dashboard.view', 'View')}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* My Programs + Action needed — real enrollments & workflow actions.
            "Action needed" holds only the lighter nudges (replies / training
            invites / enrollment prompts); it is hidden entirely when there are
            none, so the member never sees an empty box. My Programs then spans
            the full width. */}
        <div
          className={`mb-8 grid grid-cols-1 gap-6 ${
            nudgeActions.length > 0 ? 'lg:grid-cols-2' : ''
          }`}
        >
          {/* My Programs */}
          <Card className="bg-surface border border-v2border flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-paper-2 rounded-lg">
                    <ClipboardList className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-ink">
                    {t('dashboard.myPrograms', 'My Programs')}
                  </CardTitle>
                </div>
                <Link
                  to="/member/enrollments"
                  className="text-sm text-teal-600 dark:text-teal-400 hover:underline flex items-center"
                >
                  {t('dashboard.viewAll', 'View All')} <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="flex-grow">
              {enrollments.length === 0 ? (
                <div className="text-center py-6">
                  <ClipboardList className="w-10 h-10 text-ink-300 dark:text-ink-500 mx-auto mb-2" />
                  <p className="text-sm text-ink-500">
                    {t('dashboard.noProgramsYet', 'You are not enrolled in any programs yet.')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {enrollments.slice(0, 5).map((program) => (
                    <button
                      key={program.id}
                      type="button"
                      onClick={() => setDetailProgram(program)}
                      className="w-full text-left flex items-center justify-between p-3 bg-paper/50 rounded-lg border border-v2border hover:bg-paper-2 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-ink truncate">
                          {program.program_name || t('dashboard.program', 'Program')}
                        </p>
                        {program.organization_name && (
                          <span className="mt-1 inline-flex max-w-full items-center gap-1 rounded-md border border-v2border bg-paper-2 px-1.5 py-0.5 text-[11px] font-medium text-ink-500">
                            <Building2 className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{program.organization_name}</span>
                          </span>
                        )}
                        {program.enrollment_date && (
                          <p className="text-xs text-ink-500 mt-0.5">
                            {fmtShortDate(program.enrollment_date, timezone)}
                          </p>
                        )}
                      </div>
                      <span className="ml-2 flex flex-shrink-0 items-center gap-2">
                        {program.status && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {program.status}
                          </Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-ink-400" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action needed — only the lighter nudges, and only when present.
              When there are none the card is omitted entirely (no empty box,
              and My Programs above spans full width). */}
          {nudgeActions.length > 0 && (
            <Card className="bg-surface border border-v2border flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-paper-2 rounded-lg">
                    <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-ink">
                      {t('dashboard.actionNeeded', 'Action needed')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('dashboard.actionNeededDesc', 'Items waiting on you')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-grow">
                <div className="space-y-2">
                  {nudgeActions.slice(0, 5).map((action, idx) => (
                    <button
                      key={`${action.target}-${idx}`}
                      onClick={() => handleWorkflowAction(action)}
                      className="w-full text-left flex items-center justify-between p-3 bg-paper/50 rounded-lg border border-v2border hover:bg-paper-2 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-ink truncate">{action.title}</p>
                        {action.subtitle && (
                          <p className="text-xs text-ink-500 mt-0.5 truncate">{action.subtitle}</p>
                        )}
                      </div>
                      <span className="ml-2 flex flex-shrink-0 items-center text-sm font-medium text-teal-600 dark:text-teal-400">
                        {action.action || t('dashboard.open', 'Open')}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Care case — the member's care-management case episode(s). Read-only,
            fed by /api/member-portal/case-episodes (bare array). Hidden entirely
            when the member has no episodes. Placed next to My Programs since a
            case episode is the care-management wrapper around a program. */}
        {caseEpisodes.length > 0 && (
          <div className="mb-8">
            <Card className="bg-surface border border-v2border">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-paper-2 rounded-lg">
                    <HeartPulse className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-ink">
                      {t('dashboard.careCase', 'Care case')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('dashboard.careCaseDesc', 'Your care-management case')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {caseEpisodes.map((ep) => (
                    <button
                      key={ep.id}
                      type="button"
                      onClick={() => setDetailCase(ep)}
                      className={`w-full text-left p-4 rounded-lg border transition-colors hover:bg-paper-2 ${
                        ep.is_current
                          ? 'border-teal-300 dark:border-teal-700 bg-teal-50/60 dark:bg-teal-900/20'
                          : 'border-v2border bg-paper/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-ink truncate">
                              {ep.program_name || t('dashboard.careCase', 'Care case')}
                            </p>
                            {ep.is_current && (
                              <Badge className="text-[10px] bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border-teal-300 dark:border-teal-700">
                                {t('dashboard.currentCase', 'Current')}
                              </Badge>
                            )}
                          </div>
                          {(ep.case_number || ep.episode_number != null) && (
                            <p className="text-xs text-ink-500 mt-0.5">
                              {ep.case_number
                                ? `${t('dashboard.caseNumber', 'Case')} #${ep.case_number}`
                                : ''}
                              {ep.episode_number != null
                                ? `${ep.case_number ? ' · ' : ''}${t('dashboard.episode', 'Episode')} ${ep.episode_number}`
                                : ''}
                            </p>
                          )}
                          <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-ink-500">
                            {ep.organization_name && (
                              <span className="inline-flex items-center gap-1">
                                <Building2 className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{ep.organization_name}</span>
                              </span>
                            )}
                            {fmtShortDate(ep.opened_date, timezone) && (
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3 h-3 flex-shrink-0" />
                                {t('dashboard.opened', 'Opened')} {fmtShortDate(ep.opened_date, timezone)}
                              </span>
                            )}
                            {ep.care_manager_name && (
                              <span className="inline-flex items-center gap-1">
                                <Users className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{ep.care_manager_name}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="ml-2 flex flex-shrink-0 items-center gap-2">
                          {ep.status && (
                            <Badge variant="outline" className="text-xs uppercase">
                              {ep.status}
                            </Badge>
                          )}
                          <ChevronRight className="w-4 h-4 text-ink-400" />
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* To-do / Schedule — the member's tasks from /api/member-portal/tasks as
            a dated schedule (open items first, soonest due first; completed sink
            to the bottom). This is the SINGLE home for tasks — each row opens the
            task detail modal. Hidden entirely when there are no tasks. */}
        {scheduledTasks.length > 0 && (
          <div className="mb-8">
            <Card className="bg-surface border border-v2border">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-paper-2 rounded-lg">
                    <ListTodo className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-ink">
                      {t('dashboard.todoSchedule', 'To-do / Schedule')}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('dashboard.todoScheduleDesc', 'Your tasks, ordered by when they are due')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {scheduledTasks.map((task) => {
                    const done = isTaskDone(task.status);
                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => setDetailTask(task)}
                        className="w-full text-left flex items-start justify-between gap-3 p-3 bg-paper/50 rounded-lg border border-v2border hover:bg-paper-2 transition-colors"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="mt-0.5 flex-shrink-0">
                            {done ? (
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <Circle className="w-4 h-4 text-ink-300 dark:text-ink-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-medium ${
                                done ? 'text-ink-500 line-through' : 'text-ink'
                              }`}
                            >
                              {task.title || t('dashboard.task', 'Task')}
                            </p>
                            <div className="mt-0.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-ink-500">
                              {task.program_name && (
                                <span className="inline-flex items-center gap-1 min-w-0">
                                  <ClipboardList className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{task.program_name}</span>
                                </span>
                              )}
                              {fmtShortDate(task.due_at, timezone) && (
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="w-3 h-3 flex-shrink-0" />
                                  {t('dashboard.due', 'Due')} {fmtShortDate(task.due_at, timezone)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className="ml-2 flex flex-shrink-0 items-center gap-2">
                          {task.priority && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] capitalize ${priorityBadgeClass(task.priority)}`}
                            >
                              {task.priority}
                            </Badge>
                          )}
                          <ChevronRight className="w-4 h-4 text-ink-400" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Gamified Incentives Hero Section. Gated on the ACTIVE org's
            `incentives` design feature (incentives_rewards): an org that doesn't
            run incentives (e.g. LCC) hides earnings/points/achievements. Shown
            while features load (null) to avoid a flash; hidden only when
            explicitly false. */}
        {incentives && features?.incentives !== false && (
          <div className="mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Earnings Card */}
              <Card className="bg-surface border border-v2border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-sm font-medium text-green-800 dark:text-green-300">
                      {t('dashboard.totalEarnings', 'Total Earnings')}
                    </CardDescription>
                    <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <CardTitle className="text-4xl font-bold text-green-900 dark:text-green-100">
                    ${incentives.earnings.total_earnings.toFixed(2)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700 dark:text-green-300">{t('dashboard.paidOut', 'Paid Out')}:</span>
                      <span className="font-semibold text-green-900 dark:text-green-100">
                        ${incentives.earnings.total_paid.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700 dark:text-green-300">{t('dashboard.pending', 'Pending')}:</span>
                      <span className="font-semibold text-green-900 dark:text-green-100">
                        ${incentives.earnings.pending_amount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Points Card */}
              <Card className="bg-surface border border-v2border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-sm font-medium text-purple-800 dark:text-purple-300">
                      {t('dashboard.totalPoints', 'Total Points')}
                    </CardDescription>
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                      <Trophy className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                  <CardTitle className="text-4xl font-bold text-purple-900 dark:text-purple-100">
                    {incentives.earnings.total_points_earned.toLocaleString()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-1">
                    {incentives.recent_achievements.slice(0, 3).map((achievement, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700"
                      >
                        <Award className="w-3 h-3 mr-1" />
                        {achievement.name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Actions Progress Card */}
              <Card className="bg-surface border border-v2border">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('dashboard.actionProgress', 'Action Progress')}
                    </CardDescription>
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                      <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <CardTitle className="text-4xl font-bold text-blue-900 dark:text-blue-100">
                    {incentives.actions.completed}/{incentives.actions.total}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Progress
                      value={(incentives.actions.completed / incentives.actions.total) * 100}
                      className="h-2"
                    />
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-700 dark:text-blue-300">{t('dashboard.potentialEarnings', 'Potential Earnings')}:</span>
                      <span className="font-semibold text-blue-900 dark:text-blue-100">
                        ${incentives.actions.potential_earnings.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Achievements Section */}
            {incentives.recent_achievements.length > 0 && (
              <Card className="mt-6 bg-surface border border-v2border">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <CardTitle className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                      {t('dashboard.recentAchievements', 'Recent Achievements')}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-amber-700 dark:text-amber-300">
                    {t('dashboard.achievementsDescription', 'Your latest accomplishments and earned rewards')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {incentives.recent_achievements.map((achievement, index) => (
                      <div
                        key={index}
                        className="flex items-start space-x-3 p-3 bg-surface rounded-lg border border-amber-200 dark:border-amber-800"
                      >
                        <div className="flex-shrink-0">
                          <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-full">
                            {achievement.type === 'milestone' && <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                            {achievement.type === 'streak' && <TrendingUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                            {achievement.type === 'badge' && <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                            {achievement.type === 'completion' && <CheckCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">
                            {achievement.name}
                          </p>
                          {achievement.level && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {achievement.level}
                            </Badge>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-ink-500">
                              {fmtShortDate(achievement.earned_date, timezone)}
                            </span>
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                              +{achievement.points} pts
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <Link to="/member/incentives">
                      <Button variant="outline" size="sm" className="text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40">
                        <Star className="w-4 h-4 mr-2" />
                        {t('dashboard.viewAllAchievements', 'View All Achievements')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Employer Benefits Section — only when the ACTIVE org is this member's
            employer org, i.e. the active-org-scoped features explicitly enable
            benefits. A dual-org member viewing their CBO (e.g. Trust Care) must
            NOT see employer benefits (which would make CBO content like the
            Medicaid workflow look like it lives in the employer context);
            switching to the employer org surfaces them. We require
            features.benefits === true rather than falling back to `employeeData`
            or the not-yet-loaded (features === null) state, so benefits never
            flash in the CBO context before features resolve. */}
        {employeeData && features?.benefits === true && (
          <div className="mb-8">
            {/* Employer Benefits Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
                  <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-ink">
                    {t('dashboard.employerBenefits', 'Employer Benefits')}
                  </h2>
                  <p className="text-sm text-ink-500">
                    {employeeData.employer_name || t('dashboard.yourEmployer', 'Your employer-sponsored programs')}
                  </p>
                </div>
              </div>
              <Link to="/employer/navigation">
                <Button variant="outline" size="sm" className="text-indigo-600 dark:text-indigo-400 border-indigo-300 dark:border-indigo-700">
                  <Compass className="w-4 h-4 mr-2" />
                  {t('dashboard.getHelp', 'Get Help')}
                </Button>
              </Link>
            </div>

            {/* Benefits Usage Summary - OOP, HSA, FSA, EOB */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Out-of-Pocket Progress */}
              <Card className="bg-surface border border-v2border">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('dashboard.oopProgress', 'OOP Progress')}
                    </span>
                    <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs">
                      {t('dashboard.individual', 'Individual')}
                    </Badge>
                  </div>
                  {accumulator && (toNum(accumulator.deductible_total) > 0 || toNum(accumulator.oop_max) > 0) ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-ink-500">{t('dashboard.deductible', 'Deductible')}</span>
                        <span className="font-semibold text-blue-900 dark:text-blue-100">
                          {fmtCurrency(accumulator.deductible_met)} / {fmtCurrency(accumulator.deductible_total)}
                        </span>
                      </div>
                      <Progress value={pctOf(accumulator.deductible_met, accumulator.deductible_total)} className="h-2" />
                      <div className="flex justify-between text-sm">
                        <span className="text-ink-500">{t('dashboard.oopMax', 'OOP Max')}</span>
                        <span className="font-semibold text-blue-900 dark:text-blue-100">
                          {fmtCurrency(accumulator.oop_met)} / {fmtCurrency(accumulator.oop_max)}
                        </span>
                      </div>
                      <Progress value={pctOf(accumulator.oop_met, accumulator.oop_max)} className="h-2" />
                    </div>
                  ) : (
                    <p className="text-sm text-ink-500 py-2">
                      {t('dashboard.oopNotAvailable', 'Not available yet')}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* HSA Balance */}
              <Card className="bg-surface border border-v2border">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">
                      {t('dashboard.hsaBalance', 'HSA Balance')}
                    </span>
                    <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  {hsaAccount ? (
                    <>
                      <p className="font-display text-2xl font-semibold text-green-900 dark:text-green-100 mb-2">
                        {fmtCurrency(hsaAccount.balance)}
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-green-700 dark:text-green-300">
                          <span>{t('dashboard.ytdContributions', 'YTD Contributions')}</span>
                          <span className="font-medium">{fmtCurrency(hsaAccount.ytd_contributions)}</span>
                        </div>
                        <div className="flex justify-between text-green-700 dark:text-green-300">
                          <span>{t('dashboard.employerContribution', 'Employer Contribution')}</span>
                          <span className="font-medium">{fmtCurrency(hsaAccount.employer_contributions)}</span>
                        </div>
                        <div className="flex justify-between text-green-700 dark:text-green-300">
                          <span>{t('dashboard.remainingLimit', 'Remaining Limit')}</span>
                          <span className="font-medium">
                            {fmtCurrency(Math.max(0, toNum(hsaAccount.annual_limit) - toNum(hsaAccount.ytd_contributions)))}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-ink-500 py-2">
                      {t('dashboard.hsaNotAvailable', 'No HSA on file')}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* FSA Balance */}
              <Card className="bg-surface border border-v2border">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-800 dark:text-purple-300">
                      {t('dashboard.fsaBalance', 'FSA Balance')}
                    </span>
                    <DollarSign className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  {fsaAccount ? (
                    <>
                      <p className="font-display text-2xl font-semibold text-purple-900 dark:text-purple-100 mb-2">
                        {fmtCurrency(fsaAccount.balance)}
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-purple-700 dark:text-purple-300">
                          <span>{t('dashboard.annualElection', 'Annual Election')}</span>
                          <span className="font-medium">{fmtCurrency(fsaAccount.annual_limit)}</span>
                        </div>
                        <div className="flex justify-between text-purple-700 dark:text-purple-300">
                          <span>{t('dashboard.spent', 'Spent')}</span>
                          <span className="font-medium">
                            {fmtCurrency(Math.max(0, toNum(fsaAccount.annual_limit) - toNum(fsaAccount.balance)))}
                          </span>
                        </div>
                        {fmtShortDate(fsaAccount.use_by_date, timezone) && (
                          <div className="flex justify-between text-amber-600 dark:text-amber-400">
                            <span>{t('dashboard.useBy', 'Use by')} {fmtShortDate(fsaAccount.use_by_date, timezone)}</span>
                            <span className="font-medium">⚠️</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-ink-500 py-2">
                      {t('dashboard.fsaNotAvailable', 'No FSA on file')}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* EOB Documents */}
              <Card className="bg-surface border border-v2border">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      {t('dashboard.recentEOBs', 'Recent EOBs')}
                    </span>
                    <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="space-y-2">
                    {recentClaims.length > 0 ? (
                      <>
                        {recentClaims.slice(0, 3).map((claim) => {
                          const row = (
                            <div className="flex items-center justify-between p-2 bg-surface rounded border border-amber-200 dark:border-amber-700">
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-ink">{fmtShortDate(claim.service_date, timezone) || claim.claim_number || '—'}</p>
                                <p className="text-xs text-ink-500 truncate">{claim.service_type || claim.provider_name || t('dashboard.claim', 'Claim')}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">{fmtCurrency(claim.patient_responsibility)}</Badge>
                            </div>
                          );
                          return claim.eob_available && claim.eob_url ? (
                            <a key={claim.id} href={claim.eob_url} target="_blank" rel="noopener noreferrer" className="block hover:opacity-80">
                              {row}
                            </a>
                          ) : (
                            <div key={claim.id}>{row}</div>
                          );
                        })}
                        <Link to="/employer/benefits?tab=eob" className="block text-center">
                          <Button variant="ghost" size="sm" className="text-amber-600 dark:text-amber-400 text-xs w-full">
                            {t('dashboard.viewAllEOBs', 'View All EOBs')} <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <p className="text-sm text-ink-500 py-2">
                        {t('dashboard.noRecentClaims', 'No recent claims')}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Coverage Information */}
            <Card className="mb-6 border-v2border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <CardTitle className="text-lg font-semibold">{t('dashboard.coverageDetails', 'Coverage Details')}</CardTitle>
                  </div>
                  <Link to="/employer/benefits" className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center">
                    {t('dashboard.manageEnrollment', 'Manage Enrollment')} <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {planCoverages.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {planCoverages.map((cov, idx) => (
                      <div
                        key={`${cov.plan_type || 'plan'}-${idx}`}
                        className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                            {cov.plan_type || t('dashboard.planCoverage', 'Coverage')}
                          </h4>
                          {cov.status && (
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200 text-xs capitalize">
                              {cov.status}
                            </Badge>
                          )}
                        </div>
                        {cov.plan_name && (
                          <p className="text-lg font-bold text-blue-800 dark:text-blue-200 mb-2">{cov.plan_name}</p>
                        )}
                        <div className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                          {cov.carrier && (
                            <div className="flex justify-between">
                              <span>{t('dashboard.carrier', 'Carrier')}:</span>
                              <span className="font-medium">{cov.carrier}</span>
                            </div>
                          )}
                          {cov.coverage_tier && (
                            <div className="flex justify-between">
                              <span>{t('dashboard.coverage', 'Coverage')}:</span>
                              <span className="font-medium">{cov.coverage_tier}</span>
                            </div>
                          )}
                          {(toNum(cov.copay_pcp) > 0 || toNum(cov.copay_specialist) > 0) && (
                            <div className="flex justify-between">
                              <span>{t('dashboard.copay', 'Copay')}:</span>
                              <span className="font-medium">
                                {fmtCurrency(cov.copay_pcp)} PCP / {fmtCurrency(cov.copay_specialist)} {t('dashboard.specialist', 'Specialist')}
                              </span>
                            </div>
                          )}
                          {toNum(cov.deductible_individual) > 0 && (
                            <div className="flex justify-between">
                              <span>{t('dashboard.deductibleIndividual', 'Deductible (Ind.)')}:</span>
                              <span className="font-medium">{fmtCurrency(cov.deductible_individual)}</span>
                            </div>
                          )}
                          {toNum(cov.oop_max_individual) > 0 && (
                            <div className="flex justify-between">
                              <span>{t('dashboard.oopMaxIndividual', 'OOP Max (Ind.)')}:</span>
                              <span className="font-medium">{fmtCurrency(cov.oop_max_individual)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm text-ink-500 mb-2">
                      {t('dashboard.noCoverageOnFile', 'No coverage details on file yet.')}
                    </p>
                    <Link to="/employer/benefits" className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center">
                      {t('dashboard.manageEnrollment', 'Manage Enrollment')} <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                )}

                {/* Coverage Actions */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/employer/benefits">
                    <Button variant="outline" size="sm" className="text-sm">
                      <FileText className="w-4 h-4 mr-1" />
                      {t('dashboard.viewIDCards', 'View ID Cards')}
                    </Button>
                  </Link>
                  <Link to="/employer/benefits?tab=dependents">
                    <Button variant="outline" size="sm" className="text-sm">
                      <Users className="w-4 h-4 mr-1" />
                      {t('dashboard.manageDependents', 'Manage Dependents')}
                    </Button>
                  </Link>
                  <Link to="/employer/benefits?tab=providers">
                    <Button variant="outline" size="sm" className="text-sm">
                      <Building2 className="w-4 h-4 mr-1" />
                      {t('dashboard.findProviders', 'Find Providers')}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Benefit Programs */}
              <div className="lg:col-span-2">
                <Card className="h-full bg-surface border border-v2border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Gift className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        <CardTitle className="text-lg font-semibold">{t('dashboard.yourBenefits', 'Your Benefits')}</CardTitle>
                      </div>
                      <Link to="/employer/benefits" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline flex items-center">
                        {t('dashboard.viewAll', 'View All')} <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {benefitPrograms.length > 0 ? (
                      <div className="grid sm:grid-cols-2 gap-3">
                        {benefitPrograms.map((program) => (
                          <div
                            key={program.id}
                            className="p-3 bg-surface rounded-lg border border-indigo-200 dark:border-indigo-700 hover:shadow-md transition-shadow cursor-pointer"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-ink truncate">{program.program_name}</h4>
                                <p className="text-xs text-ink-500 mt-0.5">
                                  {program.program_category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </p>
                              </div>
                              {program.enrollment_status === 'ENROLLED' ? (
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 text-xs">
                                  {t('dashboard.enrolled', 'Enrolled')}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  {t('dashboard.available', 'Available')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Gift className="w-10 h-10 text-ink-300 dark:text-ink-500 mx-auto mb-2" />
                        <p className="text-sm text-ink-500">
                          {t('dashboard.noBenefitsYet', 'Benefit programs are being set up...')}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* AI Recommendations & Navigation */}
              <div className="space-y-4">
                {/* AI Recommendations */}
                <Card className="bg-surface border border-v2border">
                  <CardHeader className="pb-2">
                    <div className="flex items-center space-x-2">
                      <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <CardTitle className="text-base font-semibold">{t('dashboard.forYou', 'For You')}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {recommendations.length > 0 ? (
                      <div className="space-y-2">
                        {recommendations.slice(0, 2).map((rec) => (
                          <div
                            key={rec.id}
                            className="p-2 bg-surface rounded-lg border border-purple-200 dark:border-purple-700"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-ink truncate flex-1">
                                {rec.program_name}
                              </span>
                              <div className="flex items-center text-purple-600 dark:text-purple-400 ml-2">
                                <Star className="w-3 h-3 mr-0.5 fill-current" />
                                <span className="text-xs font-medium">{Math.round(rec.score * 100)}%</span>
                              </div>
                            </div>
                            {rec.reasons?.[0] && (
                              <p className="text-xs text-ink-500 mt-1 truncate">
                                {rec.reasons[0]}
                              </p>
                            )}
                          </div>
                        ))}
                        <Link to="/employer/recommendations" className="block text-center">
                          <Button variant="ghost" size="sm" className="text-purple-600 dark:text-purple-400 text-xs">
                            {t('dashboard.seeAllRecommendations', 'See All Recommendations')} <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <Sparkles className="w-8 h-8 text-ink-300 dark:text-ink-500 mx-auto mb-1" />
                        <p className="text-xs text-ink-500">
                          {t('dashboard.generatingRecommendations', 'Generating recommendations...')}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Navigation Assistant */}
                <Card className="bg-surface border border-v2border">
                  <CardContent className="pt-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-full">
                        <Bot className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-ink">
                          {t('dashboard.benefitsNavigator', 'Benefits Navigator')}
                        </h4>
                        <p className="text-xs text-ink-500">
                          {t('dashboard.aiPoweredHelp', 'AI-powered assistance')}
                        </p>
                      </div>
                    </div>
                    <Link to="/employer/navigation">
                      <Button className="w-full bg-v2accent hover:bg-v2accent/90 text-white">
                        <Compass className="w-4 h-4 mr-2" />
                        {t('dashboard.startNavigation', 'Start Navigation Session')}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

      {/* Workflow detail modal — opened from the "Workflow steps" section. For a
          workflow:<id> action it shows the ordered steps and current position
          (resolved from /workflow-steps); otherwise it shows the action's own
          summary. */}
      <Dialog open={!!detailAction} onOpenChange={(o) => !o && setDetailAction(null)}>
        <DialogContent className="max-w-lg">
          {detailAction && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailAction.type === 'workflow' ? (
                    <Workflow className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  ) : (
                    <ClipboardList className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  )}
                  <span>
                    {detailInstance?.workflow_name || detailAction.title}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  {detailInstance?.program_name ||
                    detailAction.subtitle ||
                    (detailAction.type === 'workflow'
                      ? t('dashboard.workflowInProgress', 'Workflow in progress')
                      : t('dashboard.taskToComplete', 'Task to complete'))}
                </DialogDescription>
              </DialogHeader>

              {detailAction.type === 'workflow' && detailInstance ? (
                <div className="space-y-4">
                  {/* Progress bar + current step */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-ink">
                        {t('dashboard.step', 'Step')} {detailInstance.current_step ?? 0}
                        {detailInstance.total_steps
                          ? ` ${t('dashboard.of', 'of')} ${detailInstance.total_steps}`
                          : ''}
                      </span>
                      {detailInstance.status && (
                        <Badge variant="outline" className="text-xs capitalize">
                          {String(detailInstance.status).replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                    <Progress
                      value={
                        detailInstance.total_steps && detailInstance.total_steps > 0
                          ? Math.min(
                              100,
                              Math.max(
                                0,
                                ((detailInstance.current_step ?? 0) /
                                  detailInstance.total_steps) *
                                  100
                              )
                            )
                          : 0
                      }
                      className="h-2"
                    />
                    {detailInstance.current_step_name && (
                      <p className="text-xs text-ink-500 mt-1">
                        {t('dashboard.currentStep', 'Current step')}:{' '}
                        {detailInstance.current_step_name}
                      </p>
                    )}
                  </div>

                  {/* Ordered steps list — current vs completed vs upcoming */}
                  <WorkflowStepsList instance={detailInstance} timezone={timezone} />
                </div>
              ) : detailAction.type === 'workflow' ? (
                // Workflow action with no matching instance detail loaded.
                <p className="text-sm text-ink-500">
                  {t(
                    'dashboard.workflowDetailUnavailable',
                    'Step details are not available right now. Your care team is progressing this workflow.'
                  )}
                </p>
              ) : (
                // task:<id> — no detail endpoint; present what the action carries.
                <p className="text-sm text-ink-500">
                  {t(
                    'dashboard.taskDetailHint',
                    'Complete this task to keep your enrollment moving. Your care team will follow up if anything else is needed.'
                  )}
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Program detail modal — opened from a "My Programs" row. Shows every
          field the enrollment carries (org, status, dates, progress, description
          / notes). If a workflow instance belongs to this program, its step
          summary is listed too. Never a dead click. */}
      <Dialog open={!!detailProgram} onOpenChange={(o) => !o && setDetailProgram(null)}>
        <DialogContent className="max-w-lg">
          {detailProgram && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  <span>{detailProgram.program_name || t('dashboard.program', 'Program')}</span>
                </DialogTitle>
                {detailProgram.organization_name && (
                  <DialogDescription className="flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {detailProgram.organization_name}
                  </DialogDescription>
                )}
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  {detailProgram.status && (
                    <Badge variant="outline" className="text-xs capitalize">
                      {detailProgram.status}
                    </Badge>
                  )}
                </div>

                {/* Progress */}
                {detailProgram.progress_percentage != null && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-ink">
                        {t('dashboard.progress', 'Progress')}
                      </span>
                      <span className="text-sm text-ink-500">
                        {Math.round(toNum(detailProgram.progress_percentage))}%
                      </span>
                    </div>
                    <Progress
                      value={Math.min(100, Math.max(0, toNum(detailProgram.progress_percentage)))}
                      className="h-2"
                    />
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {fmtShortDate(detailProgram.enrollment_date, timezone) && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.enrolled', 'Enrolled')}</p>
                      <p className="font-medium text-ink">
                        {fmtShortDate(detailProgram.enrollment_date, timezone)}
                      </p>
                    </div>
                  )}
                  {fmtShortDate(detailProgram.completion_date, timezone) && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.completed', 'Completed')}</p>
                      <p className="font-medium text-ink">
                        {fmtShortDate(detailProgram.completion_date, timezone)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Description / notes */}
                {detailProgram.program_description && (
                  <div>
                    <p className="text-xs font-medium text-ink-500 mb-1">
                      {t('dashboard.aboutProgram', 'About this program')}
                    </p>
                    <p className="text-sm text-ink whitespace-pre-line">
                      {detailProgram.program_description}
                    </p>
                  </div>
                )}
                {detailProgram.notes && (
                  <div>
                    <p className="text-xs font-medium text-ink-500 mb-1">
                      {t('dashboard.notes', 'Notes')}
                    </p>
                    <p className="text-sm text-ink whitespace-pre-line">{detailProgram.notes}</p>
                  </div>
                )}

                {/* Matching workflow step summary */}
                {programWorkflows.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-ink-500 mb-2">
                      {t('dashboard.workflowSteps', 'Workflow steps')}
                    </p>
                    <div className="space-y-3">
                      {programWorkflows.map((w) => (
                        <div key={w.id}>
                          {w.workflow_name && (
                            <p className="text-sm font-medium text-ink mb-1">{w.workflow_name}</p>
                          )}
                          <WorkflowStepsList instance={w} timezone={timezone} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Case detail modal — opened from the Care case card. Shows the episode's
          identity, dates, org and care manager, plus a member-facing "Schedule of
          tasks": the workflow step checklist for the case's program and any tasks
          linked to the case, in due order. */}
      <Dialog open={!!detailCase} onOpenChange={(o) => !o && setDetailCase(null)}>
        <DialogContent className="max-w-lg">
          {detailCase && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HeartPulse className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                  <span>{detailCase.program_name || t('dashboard.careCase', 'Care case')}</span>
                </DialogTitle>
                <DialogDescription>
                  {[
                    detailCase.case_number ? `${t('dashboard.caseNumber', 'Case')} #${detailCase.case_number}` : '',
                    detailCase.episode_number != null
                      ? `${t('dashboard.episode', 'Episode')} ${detailCase.episode_number}`
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' · ') || t('dashboard.careCaseDesc', 'Your care-management case')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="flex flex-wrap items-center gap-2">
                  {detailCase.status && (
                    <Badge variant="outline" className="text-xs uppercase">
                      {detailCase.status}
                    </Badge>
                  )}
                  {detailCase.is_current && (
                    <Badge className="text-[10px] bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border-teal-300 dark:border-teal-700">
                      {t('dashboard.currentCase', 'Current')}
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {detailCase.organization_name && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.organization', 'Organization')}</p>
                      <p className="font-medium text-ink">{detailCase.organization_name}</p>
                    </div>
                  )}
                  {detailCase.care_manager_name && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.careManager', 'Care manager')}</p>
                      <p className="font-medium text-ink">{detailCase.care_manager_name}</p>
                    </div>
                  )}
                  {fmtShortDate(detailCase.opened_date, timezone) && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.opened', 'Opened')}</p>
                      <p className="font-medium text-ink">{fmtShortDate(detailCase.opened_date, timezone)}</p>
                    </div>
                  )}
                  {fmtShortDate(detailCase.closed_date, timezone) && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.closed', 'Closed')}</p>
                      <p className="font-medium text-ink">{fmtShortDate(detailCase.closed_date, timezone)}</p>
                    </div>
                  )}
                  {(fmtShortDate(detailCase.service_period_start, timezone) ||
                    fmtShortDate(detailCase.service_period_end, timezone)) && (
                    <div className="col-span-2">
                      <p className="text-xs text-ink-500">{t('dashboard.servicePeriod', 'Service period')}</p>
                      <p className="font-medium text-ink">
                        {fmtShortDate(detailCase.service_period_start, timezone) || '—'}
                        {' – '}
                        {fmtShortDate(detailCase.service_period_end, timezone) || t('dashboard.ongoing', 'Ongoing')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Schedule of tasks — workflow step checklist + linked tasks */}
                <div>
                  <p className="text-sm font-semibold text-ink flex items-center gap-2 mb-2">
                    <CalendarClock className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                    {t('dashboard.scheduleOfTasks', 'Schedule of tasks')}
                  </p>
                  {caseWorkflows.length === 0 && caseTasks.length === 0 ? (
                    <p className="text-sm text-ink-500">
                      {t('dashboard.noCaseSchedule', 'No scheduled steps or tasks for this case yet.')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {caseWorkflows.map((w) => (
                        <div key={w.id}>
                          {w.workflow_name && (
                            <p className="text-xs font-medium text-ink-500 mb-1">{w.workflow_name}</p>
                          )}
                          <WorkflowStepsList instance={w} timezone={timezone} />
                        </div>
                      ))}
                      {caseTasks.length > 0 && (
                        <div className="space-y-2">
                          {caseTasks.map((task) => {
                            const done = isTaskDone(task.status);
                            return (
                              <button
                                key={task.id}
                                type="button"
                                onClick={() => {
                                  setDetailCase(null);
                                  setDetailTask(task);
                                }}
                                className="w-full text-left flex items-start gap-3 p-3 rounded-lg border border-v2border bg-paper/50 hover:bg-paper-2 transition-colors"
                              >
                                <div className="mt-0.5 flex-shrink-0">
                                  {done ? (
                                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-ink-300 dark:text-ink-500" />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p
                                    className={`text-sm font-medium ${
                                      done ? 'text-ink-500 line-through' : 'text-ink'
                                    }`}
                                  >
                                    {task.title || t('dashboard.task', 'Task')}
                                  </p>
                                  {fmtShortDate(task.due_at, timezone) && (
                                    <span className="inline-flex items-center gap-1 text-xs text-ink-500 mt-0.5">
                                      <Clock className="w-3 h-3 flex-shrink-0" />
                                      {t('dashboard.due', 'Due')} {fmtShortDate(task.due_at, timezone)}
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Task detail modal — opened from the To-do schedule, a task:<id> action,
          or a case's schedule. Shows the task's full detail plus program/case
          context (resolved from /tasks). */}
      <Dialog open={!!detailTask} onOpenChange={(o) => !o && setDetailTask(null)}>
        <DialogContent className="max-w-lg">
          {detailTask && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  <span>{detailTask.title || t('dashboard.task', 'Task')}</span>
                </DialogTitle>
                {detailTask.program_name && (
                  <DialogDescription className="flex items-center gap-1">
                    <ClipboardList className="w-3.5 h-3.5" />
                    {detailTask.program_name}
                  </DialogDescription>
                )}
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  {detailTask.status && (
                    <Badge variant="outline" className="text-xs capitalize">
                      {String(detailTask.status).replace(/_/g, ' ')}
                    </Badge>
                  )}
                  {detailTask.priority && (
                    <Badge
                      variant="outline"
                      className={`text-xs capitalize inline-flex items-center gap-1 ${priorityBadgeClass(
                        detailTask.priority
                      )}`}
                    >
                      <Flag className="w-3 h-3" />
                      {detailTask.priority}
                    </Badge>
                  )}
                  {detailTask.task_type && (
                    <Badge variant="outline" className="text-xs capitalize">
                      {String(detailTask.task_type).replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>

                {detailTask.progress_percentage != null && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-ink">
                        {t('dashboard.progress', 'Progress')}
                      </span>
                      <span className="text-sm text-ink-500">
                        {Math.round(toNum(detailTask.progress_percentage))}%
                      </span>
                    </div>
                    <Progress
                      value={Math.min(100, Math.max(0, toNum(detailTask.progress_percentage)))}
                      className="h-2"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {fmtShortDate(detailTask.due_at, timezone) && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.due', 'Due')}</p>
                      <p className="font-medium text-ink inline-flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {fmtShortDate(detailTask.due_at, timezone)}
                      </p>
                    </div>
                  )}
                  {fmtShortDate(detailTask.completed_at, timezone) && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.completed', 'Completed')}</p>
                      <p className="font-medium text-ink">
                        {fmtShortDate(detailTask.completed_at, timezone)}
                      </p>
                    </div>
                  )}
                  {detailTask.organization_name && (
                    <div>
                      <p className="text-xs text-ink-500">{t('dashboard.organization', 'Organization')}</p>
                      <p className="font-medium text-ink">{detailTask.organization_name}</p>
                    </div>
                  )}
                </div>

                {detailTask.description && (
                  <div>
                    <p className="text-xs font-medium text-ink-500 mb-1">
                      {t('dashboard.details', 'Details')}
                    </p>
                    <p className="text-sm text-ink whitespace-pre-line">{detailTask.description}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Support is provided by the native support-widget.js mounted globally
          from index.html (single, neutral-styled widget). No per-page widget. */}
    </PageContainer>
  );
}
